import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getTeams, createTeam, updateTeam, deleteTeam } from '../services/api'
import { getUsers } from '../services/users'
import LoadingSpinner from '../components/common/LoadingSpinner'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Avatar,
  AvatarGroup,
  Chip,
  Paper,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material'
import GroupsIcon from '@mui/icons-material/Groups'
import PersonIcon from '@mui/icons-material/Person'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import GroupAddIcon from '@mui/icons-material/GroupAdd'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

/* ───────────────────────── Types ───────────────────────── */

interface TeamMember {
  id: string
  name: string
  email: string
  role: string
}

interface Team {
  _id?: string
  id?: string
  name: string
  members?: TeamMember[]
  memberIds?: string[]
  createdAt?: string
  updatedAt?: string
}

interface User {
  id?: string
  _id?: string
  name: string
  email: string
  role: string
}

/* ───────────────────────── Helpers ───────────────────────── */

function uid(user: User): string {
  return String(user.id || user._id || '')
}

function tid(team: Team): string {
  return String(team.id || team._id || '')
}

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('')
}

function roleColor(role: string): string {
  switch (role) {
    case 'super_admin': return '#7C3AED'
    case 'admin': return '#2563EB'
    default: return '#6B7280'
  }
}

function roleBg(role: string): string {
  switch (role) {
    case 'super_admin': return '#EDE9FE'
    case 'admin': return '#DBEAFE'
    default: return '#F3F4F6'
  }
}

/* ───────────────────────── Component ───────────────────────── */

function TeamsScreen(): React.JSX.Element {
  const { t, i18n } = useTranslation()

  /* Data */
  const [teams, setTeams] = useState<Team[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)

  /* Selection */
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)

  /* Create dialog */
  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createSelectedUsers, setCreateSelectedUsers] = useState<string[]>([])
  const [createSearch, setCreateSearch] = useState('')

  /* Rename dialog */
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameId, setRenameId] = useState('')
  const [renameName, setRenameName] = useState('')

  /* Delete confirmation */
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteId, setDeleteId] = useState('')
  const [deleteName, setDeleteName] = useState('')

  /* Member management dialog */
  const [membersOpen, setMembersOpen] = useState(false)
  const [membersTeamId, setMembersTeamId] = useState('')
  const [membersSearch, setMembersSearch] = useState('')
  const [pendingMemberIds, setPendingMemberIds] = useState<string[]>([])
  const [membersDirty, setMembersDirty] = useState(false)

  /* Snackbar */
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  })

  /* ─── Data loading ─── */

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      const [teamsRes, usersRes] = await Promise.all([getTeams(), getUsers()])
      setTeams(teamsRes.data)
      const usersData: User[] = Array.isArray(usersRes.data)
        ? usersRes.data
        : Array.isArray((usersRes.data as any)?.data)
          ? (usersRes.data as any).data
          : []
      setUsers(usersData)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  /* ─── Stats ─── */

  const allAssignedIds = useMemo(() => {
    const ids = new Set<string>()
    teams.forEach(team => (team.memberIds || []).forEach(id => ids.add(String(id))))
    return ids
  }, [teams])

  const unassignedUsers = useMemo(
    () => users.filter(u => !allAssignedIds.has(uid(u))),
    [users, allAssignedIds],
  )

  /* Selected team */
  const selectedTeam = useMemo(
    () => teams.find(t => tid(t) === selectedTeamId) || null,
    [teams, selectedTeamId],
  )

  // Auto-select first team if none selected
  useEffect(() => {
    if (!selectedTeamId && teams.length > 0) {
      setSelectedTeamId(tid(teams[0]))
    }
  }, [teams, selectedTeamId])

  // Keep selection valid
  useEffect(() => {
    if (selectedTeamId && !teams.find(t => tid(t) === selectedTeamId)) {
      setSelectedTeamId(teams.length > 0 ? tid(teams[0]) : null)
    }
  }, [teams, selectedTeamId])

  /* ─── Helpers ─── */

  function showSnack(message: string, severity: 'success' | 'error' = 'success') {
    setSnackbar({ open: true, message, severity })
  }

  /* ─── Create Team ─── */

  async function handleCreateTeam(): Promise<void> {
    if (!createName.trim()) {
      showSnack(t('form.error.required'), 'error')
      return
    }
    try {
      const res = await createTeam({ name: createName.trim(), memberIds: createSelectedUsers })
      await loadData()
      setCreateName('')
      setCreateSelectedUsers([])
      setCreateSearch('')
      setCreateOpen(false)
      setSelectedTeamId(res.data.id || res.data._id)
      showSnack(t('team.createSuccess'))
    } catch {
      showSnack(t('common.error'), 'error')
    }
  }

  /* ─── Rename Team ─── */

  function openRename(teamId: string, name: string) {
    setRenameId(teamId)
    setRenameName(name)
    setRenameOpen(true)
  }

  async function handleRenameTeam(): Promise<void> {
    if (!renameName.trim()) {
      showSnack(t('form.error.required'), 'error')
      return
    }
    try {
      await updateTeam(renameId, { name: renameName.trim() })
      await loadData()
      setRenameOpen(false)
      showSnack(t('team.renameSuccess'))
    } catch {
      showSnack(t('common.error'), 'error')
    }
  }

  /* ─── Delete Team ─── */

  function openDeleteDialog(teamId: string, name: string) {
    setDeleteId(teamId)
    setDeleteName(name)
    setDeleteOpen(true)
  }

  async function handleDeleteTeam(): Promise<void> {
    try {
      await deleteTeam(deleteId)
      await loadData()
      setDeleteOpen(false)
      if (selectedTeamId === deleteId) setSelectedTeamId(null)
      showSnack(t('team.deleteSuccess'))
    } catch {
      showSnack(t('common.error'), 'error')
    }
  }

  /* ─── Member Management ─── */

  function openMembersDialog(teamId: string) {
    const team = teams.find(t => tid(t) === teamId)
    const memberIds = (team?.memberIds || []).map(String)
    setMembersTeamId(teamId)
    setPendingMemberIds(memberIds)
    setMembersDirty(false)
    setMembersSearch('')
    setMembersOpen(true)
  }

  function addMember(userId: string) {
    setPendingMemberIds(prev => [...prev, userId])
    setMembersDirty(true)
  }

  function removeMember(userId: string) {
    setPendingMemberIds(prev => prev.filter(id => id !== userId))
    setMembersDirty(true)
  }

  async function handleSaveMembers(): Promise<void> {
    try {
      await updateTeam(membersTeamId, { memberIds: pendingMemberIds })
      await loadData()
      setMembersOpen(false)
      showSnack(t('team.saveSuccess'))
    } catch {
      showSnack(t('common.error'), 'error')
    }
  }

  const membersTeam = useMemo(
    () => teams.find(t => tid(t) === membersTeamId),
    [teams, membersTeamId],
  )

  const availableForTransfer = useMemo(() => {
    const valid = users.filter(u => uid(u) && !pendingMemberIds.includes(uid(u)))
    if (!membersSearch.trim()) return valid
    const q = membersSearch.toLowerCase()
    return valid.filter(u =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    )
  }, [users, pendingMemberIds, membersSearch])

  const currentMembers = useMemo(
    () => pendingMemberIds.map(id => users.find(u => uid(u) === id)).filter(Boolean) as User[],
    [pendingMemberIds, users],
  )

  /* Create dialog filtered users */
  const createFilteredUsers = useMemo(() => {
    const valid = users.filter(u => uid(u))
    if (!createSearch.trim()) return valid
    const q = createSearch.toLowerCase()
    return valid.filter(u =>
      u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    )
  }, [users, createSearch])

  /* ─── Render ─── */

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            {t('navigation.teams')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('team.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setCreateName(''); setCreateSelectedUsers([]); setCreateSearch(''); setCreateOpen(true) }}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          {t('team.create')}
        </Button>
      </Box>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Stats */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3, mb: 4 }}>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#FEF3C7', color: '#D97706', width: 44, height: 44 }}>
                <GroupsIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>{teams.length}</Typography>
                <Typography variant="caption" color="text.secondary">{t('team.totalTeams')}</Typography>
              </Box>
            </Paper>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#D1FAE5', color: '#059669', width: 44, height: 44 }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>{allAssignedIds.size}</Typography>
                <Typography variant="caption" color="text.secondary">{t('team.assignedUsers')}</Typography>
              </Box>
            </Paper>
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#FEE2E2', color: '#DC2626', width: 44, height: 44 }}>
                <PersonOffIcon />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>{unassignedUsers.length}</Typography>
                <Typography variant="caption" color="text.secondary">{t('team.unassignedUsers')}</Typography>
              </Box>
            </Paper>
          </Box>

          {teams.length === 0 ? (
            /* Empty state */
            <Paper elevation={0} sx={{ p: 6, borderRadius: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
              <GroupsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('team.noTeams')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                {t('team.createFirst')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setCreateName(''); setCreateSelectedUsers([]); setCreateSearch(''); setCreateOpen(true) }}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                {t('team.create')}
              </Button>
            </Paper>
          ) : (
            /* Main two-panel layout */
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px 1fr' }, gap: 3, alignItems: 'start' }}>
              {/* Left panel: Team list */}
              <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                    {t('team.allTeams')}
                  </Typography>
                </Box>
                <List sx={{ py: 0, maxHeight: 600, overflowY: 'auto' }}>
                  {teams.map((team, index) => {
                    const teamId = tid(team)
                    const isSelected = teamId === selectedTeamId
                    const memberCount = (team.memberIds || []).length
                    return (
                      <React.Fragment key={teamId}>
                        <ListItem
                          onClick={() => setSelectedTeamId(teamId)}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: isSelected ? 'rgba(246, 180, 0, 0.08)' : 'transparent',
                            borderLeft: isSelected ? '3px solid #F6B400' : '3px solid transparent',
                            '&:hover': { bgcolor: isSelected ? 'rgba(246, 180, 0, 0.12)' : 'action.hover' },
                            py: 1.5,
                            px: 2,
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: 44 }}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: isSelected ? '#F6B400' : '#F0E2C6', color: isSelected ? '#fff' : '#6B4A2B', fontSize: 14, fontWeight: 700 }}>
                              {initials(team.name)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={isSelected ? 700 : 500} noWrap>
                                {team.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {t('team.memberCount', { count: memberCount })}
                              </Typography>
                            }
                          />
                          <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: 10, border: '1px solid #fff' } }}>
                            {(team.members || []).slice(0, 3).map(m => (
                              <Avatar key={m.id} sx={{ bgcolor: '#F6B400', color: '#2B1A0F' }}>
                                {initials(m.name)}
                              </Avatar>
                            ))}
                          </AvatarGroup>
                        </ListItem>
                        {index < teams.length - 1 && <Divider component="li" />}
                      </React.Fragment>
                    )
                  })}
                </List>
              </Paper>

              {/* Right panel: Team detail */}
              {selectedTeam ? (
                <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                  {/* Detail header */}
                  <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(246, 180, 0, 0.04)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 48, height: 48, bgcolor: '#F6B400', color: '#fff', fontWeight: 700, fontSize: 18 }}>
                        {initials(selectedTeam.name)}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={700}>{selectedTeam.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('team.memberCount', { count: (selectedTeam.memberIds || []).length })}
                          {selectedTeam.createdAt && (
                            <> &middot; {t('team.createdOn', { date: new Date(selectedTeam.createdAt).toLocaleDateString(i18n.language) })}</>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={t('team.manageMembers')}>
                        <IconButton onClick={() => openMembersDialog(tid(selectedTeam))} color="primary" size="small">
                          <GroupAddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('team.rename')}>
                        <IconButton onClick={() => openRename(tid(selectedTeam), selectedTeam.name)} size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('team.delete')}>
                        <IconButton onClick={() => openDeleteDialog(tid(selectedTeam), selectedTeam.name)} size="small" color="error">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Members list */}
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                        {t('team.members')}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<PersonAddIcon />}
                        onClick={() => openMembersDialog(tid(selectedTeam))}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {t('team.addMembers')}
                      </Button>
                    </Box>

                    {(selectedTeam.members || []).length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <PersonOffIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('team.noMembers')}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PersonAddIcon />}
                          onClick={() => openMembersDialog(tid(selectedTeam))}
                          sx={{ textTransform: 'none', fontWeight: 600, mt: 1 }}
                        >
                          {t('team.addMembers')}
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(selectedTeam.members || []).map(member => (
                          <Box
                            key={member.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              p: 1.5,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <Avatar sx={{ width: 36, height: 36, bgcolor: '#F6B400', color: '#2B1A0F', fontSize: 13, fontWeight: 700 }}>
                              {initials(member.name)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {member.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {member.email}
                              </Typography>
                            </Box>
                            <Chip
                              label={t(`users.roles.${member.role}`, { defaultValue: member.role })}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: 11,
                                bgcolor: roleBg(member.role),
                                color: roleColor(member.role),
                                border: 'none',
                                height: 24,
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Paper>
              ) : (
                <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 6, textAlign: 'center' }}>
                  <GroupsIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    {t('team.selectTeam')}
                  </Typography>
                </Paper>
              )}
            </Box>
          )}
        </>
      )}

      {/* ════════════ Create Team Dialog ════════════ */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <GroupAddIcon color="primary" />
          {t('team.create')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('form.label.teamName')}
            fullWidth
            value={createName}
            onChange={e => setCreateName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && createName.trim()) handleCreateTeam() }}
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
            {t('team.initialMembers')}
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder={t('team.searchUsers')}
            value={createSearch}
            onChange={e => setCreateSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment>
              ),
            }}
            sx={{ mb: 1.5 }}
          />

          {/* Selected chips */}
          {createSelectedUsers.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
              {createSelectedUsers.map(userId => {
                const user = users.find(u => uid(u) === userId)
                return user ? (
                  <Chip
                    key={userId}
                    label={user.name}
                    size="small"
                    onDelete={() => setCreateSelectedUsers(prev => prev.filter(id => id !== userId))}
                    sx={{ fontWeight: 500 }}
                    avatar={<Avatar sx={{ bgcolor: '#F6B400', color: '#2B1A0F', fontSize: 10, width: 22, height: 22 }}>{initials(user.name)}</Avatar>}
                  />
                ) : null
              })}
            </Box>
          )}

          <Box sx={{ maxHeight: 240, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            {createFilteredUsers.filter(u => !createSelectedUsers.includes(uid(u))).map(user => (
              <Box
                key={uid(user)}
                onClick={() => setCreateSelectedUsers(prev => [...prev, uid(user)])}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 600, bgcolor: '#F0E2C6', color: '#6B4A2B' }}>
                  {initials(user.name)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>{user.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>{user.email}</Typography>
                </Box>
                <AddIcon fontSize="small" color="disabled" />
              </Box>
            ))}
            {createFilteredUsers.filter(u => !createSelectedUsers.includes(uid(u))).length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('team.noUsersFound')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>
            {t('button.cancel.text')}
          </Button>
          <Button onClick={handleCreateTeam} variant="contained" disabled={!createName.trim()} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('team.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════ Rename Team Dialog ════════════ */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('team.rename')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('form.label.teamName')}
            fullWidth
            value={renameName}
            onChange={e => setRenameName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleRenameTeam() }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setRenameOpen(false)} sx={{ textTransform: 'none' }}>
            {t('button.cancel.text')}
          </Button>
          <Button onClick={handleRenameTeam} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('button.save.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════ Delete Confirmation Dialog ════════════ */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>
          {t('team.delete')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('team.deleteConfirmMessage', { name: deleteName })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} sx={{ textTransform: 'none' }}>
            {t('button.cancel.text')}
          </Button>
          <Button onClick={handleDeleteTeam} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 600 }}>
            {t('team.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════ Manage Members Dialog ════════════ */}
      <Dialog
        open={membersOpen}
        onClose={() => setMembersOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <GroupsIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              {membersTeam
                ? t('team.manageMembersFor', { name: membersTeam.name })
                : t('team.manageMembers')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setMembersOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', minHeight: 420 }}>
            {/* Left: Available users */}
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                  {t('team.available')}
                </Typography>
                <Chip label={availableForTransfer.length} size="small" sx={{ fontWeight: 600, height: 22 }} />
              </Box>
              <TextField
                size="small"
                fullWidth
                placeholder={t('team.searchUsers')}
                value={membersSearch}
                onChange={e => setMembersSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon fontSize="small" color="disabled" /></InputAdornment>
                  ),
                }}
              />
              <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 340 }}>
                {availableForTransfer.map(user => {
                  const userId = uid(user)
                  return (
                    <Box
                      key={userId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        mb: 0.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: 'rgba(246, 180, 0, 0.06)', borderColor: '#F6B400' },
                      }}
                      onClick={() => addMember(userId)}
                    >
                      <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 600, bgcolor: '#F0E2C6', color: '#6B4A2B' }}>
                        {initials(user.name)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>{user.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{user.email}</Typography>
                      </Box>
                      <Tooltip title={t('team.addToTeam')}>
                        <ArrowForwardIcon fontSize="small" color="disabled" />
                      </Tooltip>
                    </Box>
                  )
                })}
                {availableForTransfer.length === 0 && (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {membersSearch
                        ? t('team.noUsersFound')
                        : t('team.allAssigned')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Center divider */}
            <Divider orientation="vertical" flexItem />

            {/* Right: Current members */}
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                  {t('team.currentMembers')}
                </Typography>
                <Chip
                  label={pendingMemberIds.length}
                  size="small"
                  sx={{ fontWeight: 600, height: 22, bgcolor: '#F6B400', color: '#2B1A0F' }}
                />
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: 370 }}>
                {currentMembers.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <PersonOffIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t('team.noMembers')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('team.clickToAdd')}
                    </Typography>
                  </Box>
                ) : (
                  currentMembers.map(user => {
                    const userId = uid(user)
                    return (
                      <Box
                        key={userId}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 1.5,
                          py: 1,
                          borderRadius: 1.5,
                          mb: 0.5,
                          border: '1px solid',
                          borderColor: '#F6B400',
                          bgcolor: 'rgba(246, 180, 0, 0.04)',
                        }}
                      >
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, fontWeight: 600, bgcolor: '#F6B400', color: '#2B1A0F' }}>
                          {initials(user.name)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{user.name}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>{user.email}</Typography>
                        </Box>
                        <Chip
                          label={t(`users.roles.${user.role}`, { defaultValue: user.role })}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: 10, bgcolor: roleBg(user.role), color: roleColor(user.role), height: 20, mr: 0.5 }}
                        />
                        <Tooltip title={t('team.removeMember')}>
                          <IconButton
                            size="small"
                            onClick={() => removeMember(userId)}
                            sx={{ '&:hover': { color: '#EF4444' } }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )
                  })
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setMembersOpen(false)} sx={{ textTransform: 'none' }}>
            {t('button.cancel.text')}
          </Button>
          <Button
            onClick={handleSaveMembers}
            variant="contained"
            disabled={!membersDirty}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('button.save.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════ Snackbar ════════════ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default TeamsScreen
